import api from './api';
import {
  TenantAdmin,
  TenantAdminCreateRequest,
  TenantAdminPagedResult,
  TenantAdminSearchParams,
  TenantAdminUpdateRequest,
  TenantAdminUserAssignmentDetail,
  TenantAdminUserAssignmentInput,
  TenantDbConnectionTestResult,
  TenantDbVersion,
  TenantDbVersionCode,
  TenantMigrateJobStatus,
  TenantMigrateToWebJob,
  TenantMigrateToWebStart,
  TenantMigrateToWebStep,
} from '../types';

const base = '/api/admin/tenants';

export interface TenantAdminAccessStatus {
  gateRequired: boolean;
}

export interface TenantAdminVerifyAccessResult {
  gateToken: string;
  expiresAt: string;
  gateRequired: boolean;
}

function normalizeTenant(raw: Record<string, unknown>): TenantAdmin {
  return {
    id: Number(raw.id),
    code: String(raw.code ?? '').trim(),
    name: String(raw.name ?? '').trim(),
    domain: String(raw.domain ?? '').trim() || undefined,
    dbConnectionString: String(raw.dbConnectionString ?? raw.db_connection_string ?? '').trim(),
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
    plan: String(raw.plan ?? '').trim() || undefined,
    createdAt: (raw.createdAt ?? raw.created_at) as string | undefined,
    expiresAt: (raw.expiresAt ?? raw.expires_at) as string | undefined,
    parentTenantId: parseOptionalId(raw.parentTenantId ?? raw.parent_tenant_id),
    parentTenantCode: String(raw.parentTenantCode ?? raw.parent_tenant_code ?? '').trim() || undefined,
  };
}

function mapTenantPayload(body: TenantAdminCreateRequest | TenantAdminUpdateRequest) {
  return {
    code: body.code.trim(),
    name: body.name.trim(),
    domain: body.domain?.trim() || undefined,
    dbConnectionString: body.dbConnectionString?.trim() || undefined,
    isActive: body.isActive ?? true,
    plan: body.plan?.trim() || undefined,
    expiresAt: body.expiresAt || undefined,
    parentTenantId: body.parentTenantId ?? null,
  };
}

const tenantAdminService = {
  getAccessStatus: async (): Promise<TenantAdminAccessStatus> => {
    const response = await api.get(`${base}/access-status`);
    const data = response.data as Record<string, unknown>;
    return {
      gateRequired: Boolean(data.gateRequired ?? data.gate_required ?? false),
    };
  },

  verifyAccess: async (password: string): Promise<TenantAdminVerifyAccessResult> => {
    const response = await api.post(`${base}/verify-access`, { password });
    const data = response.data as Record<string, unknown>;
    return {
      gateToken: String(data.gateToken ?? data.gate_token ?? ''),
      expiresAt: String(data.expiresAt ?? data.expires_at ?? ''),
      gateRequired: Boolean(data.gateRequired ?? data.gate_required ?? true),
    };
  },

  listPaged: async (params: TenantAdminSearchParams): Promise<TenantAdminPagedResult> => {
    const response = await api.get(base, { params });
    const data = response.data;
    const pageNumber = params.pageNumber || 1;
    const pageSize = params.pageSize || 10;

    if (Array.isArray(data)) {
      const items = data.map((row) => normalizeTenant(row as Record<string, unknown>));
      return {
        items,
        totalCount: items.length,
        pageNumber,
        pageSize,
        totalPages: Math.ceil(items.length / pageSize) || 0,
      };
    }

    if (data && typeof data === 'object' && 'items' in data) {
      const paged = data as TenantAdminPagedResult;
      return {
        ...paged,
        items: (paged.items as unknown[]).map((row) =>
          normalizeTenant(row as Record<string, unknown>),
        ),
      };
    }

    return {
      items: [],
      totalCount: 0,
      pageNumber,
      pageSize,
      totalPages: 0,
    };
  },

  getById: async (id: number): Promise<TenantAdmin> => {
    const response = await api.get(`${base}/${id}`);
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  create: async (body: TenantAdminCreateRequest): Promise<TenantAdmin> => {
    const response = await api.post(base, mapTenantPayload(body));
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  update: async (id: number, body: TenantAdminUpdateRequest): Promise<TenantAdmin> => {
    const response = await api.put(`${base}/${id}`, mapTenantPayload(body));
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  setActiveStatus: async (id: number, isActive: boolean): Promise<TenantAdmin> => {
    const response = await api.patch(`${base}/${id}/status`, { isActive });
    return normalizeTenant(response.data as Record<string, unknown>);
  },

  getTenantUsers: async (tenantId: number): Promise<TenantAdminUserAssignmentDetail[]> => {
    const response = await api.get(`${base}/${tenantId}/users`);
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeTenantUser(row as Record<string, unknown>));
  },

  testConnection: async (dbConnectionString: string): Promise<TenantDbConnectionTestResult> => {
    const response = await api.post(`${base}/test-connection`, { dbConnectionString });
    const data = response.data as Record<string, unknown>;
    const sqlErrorNumber = data.sqlErrorNumber ?? data.sql_error_number;
    return {
      ok: Boolean(data.ok),
      category: normalizeConnectionTestCategory(data.category),
      message: String(data.message ?? ''),
      detail: String(data.detail ?? '').trim() || undefined,
      sqlErrorNumber:
        sqlErrorNumber === undefined || sqlErrorNumber === null ? undefined : Number(sqlErrorNumber),
      server: String(data.server ?? '').trim() || undefined,
      database: String(data.database ?? '').trim() || undefined,
    };
  },

  getDbVersion: async (id: number): Promise<TenantDbVersion> => {
    const response = await api.get(`${base}/${id}/db-version`);
    const data = response.data as Record<string, unknown>;
    const rawVersion = String(data.version ?? 'Unknown');
    const version: TenantDbVersionCode =
      rawVersion === 'eOne' || rawVersion === 'OneLegacy' ? rawVersion : 'Unknown';
    const reasonsRaw = data.reasons;
    const reasons = Array.isArray(reasonsRaw)
      ? reasonsRaw.map((item) => String(item)).filter(Boolean)
      : [];
    return {
      version,
      reasons,
      isSecondary: Boolean(data.isSecondary ?? data.is_secondary ?? false),
      parentTenantId: parseOptionalId(data.parentTenantId ?? data.parent_tenant_id),
      parentTenantCode: String(data.parentTenantCode ?? data.parent_tenant_code ?? '').trim() || undefined,
      parentDatabaseName:
        String(data.parentDatabaseName ?? data.parent_database_name ?? '').trim() || undefined,
    };
  },

  startMigrateToWeb: async (id: number): Promise<TenantMigrateToWebStart> => {
    const response = await api.post(`${base}/${id}/migrate-to-web`);
    const data = response.data as Record<string, unknown>;
    return {
      jobId: String(data.jobId ?? data.job_id ?? ''),
      alreadyRunning: Boolean(data.alreadyRunning ?? data.already_running ?? false),
    };
  },

  getMigrateJob: async (jobId: string): Promise<TenantMigrateToWebJob> => {
    const response = await api.get(`${base}/migrate-jobs/${jobId}`);
    return normalizeMigrateJob(response.data as Record<string, unknown>);
  },

  setTenantUsers: async (
    tenantId: number,
    users: TenantAdminUserAssignmentInput[],
  ): Promise<TenantAdminUserAssignmentDetail[]> => {
    const response = await api.put(`${base}/${tenantId}/users`, {
      users: users.map((user) => ({
        userId: user.userId,
        role: user.role.trim() || 'User',
        isDefault: user.isDefault,
      })),
    });
    const data = response.data;
    if (!Array.isArray(data)) return [];
    return data.map((row) => normalizeTenantUser(row as Record<string, unknown>));
  },
};

function parseOptionalId(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function normalizeConnectionTestCategory(raw: unknown): TenantDbConnectionTestResult['category'] {
  const value = String(raw ?? 'other');
  if (
    value === 'ok' ||
    value === 'server' ||
    value === 'credentials' ||
    value === 'database' ||
    value === 'timeout' ||
    value === 'certificate' ||
    value === 'invalid'
  ) {
    return value;
  }
  return 'other';
}

function normalizeJobStatus(raw: unknown): TenantMigrateJobStatus {
  const value = String(raw ?? 'Pending');
  if (value === 'Running' || value === 'Succeeded' || value === 'Failed') return value;
  return 'Pending';
}

function normalizeMigrateJob(raw: Record<string, unknown>): TenantMigrateToWebJob {
  const stepsRaw = Array.isArray(raw.steps) ? raw.steps : [];
  const steps: TenantMigrateToWebStep[] = stepsRaw.map((row) => {
    const step = row as Record<string, unknown>;
    return {
      key: String(step.key ?? ''),
      title: String(step.title ?? ''),
      status: normalizeJobStatus(step.status),
      message: String(step.message ?? '').trim() || undefined,
    };
  });

  return {
    jobId: String(raw.jobId ?? raw.job_id ?? ''),
    tenantId: Number(raw.tenantId ?? raw.tenant_id ?? 0),
    tenantCode: String(raw.tenantCode ?? raw.tenant_code ?? ''),
    status: normalizeJobStatus(raw.status),
    currentStep: String(raw.currentStep ?? raw.current_step ?? '').trim() || undefined,
    steps,
    startedAt: String(raw.startedAt ?? raw.started_at ?? ''),
    finishedAt: String(raw.finishedAt ?? raw.finished_at ?? '').trim() || undefined,
    error: String(raw.error ?? '').trim() || undefined,
    errorDetail: String(raw.errorDetail ?? raw.error_detail ?? '').trim() || undefined,
    isSecondary: Boolean(raw.isSecondary ?? raw.is_secondary ?? false),
    parentTenantCode: String(raw.parentTenantCode ?? raw.parent_tenant_code ?? '').trim() || undefined,
    parentDatabaseName:
      String(raw.parentDatabaseName ?? raw.parent_database_name ?? '').trim() || undefined,
  };
}

function normalizeTenantUser(raw: Record<string, unknown>): TenantAdminUserAssignmentDetail {
  return {
    userId: Number(raw.userId ?? raw.user_id),
    username: String(raw.username ?? '').trim(),
    displayName: String(raw.displayName ?? raw.display_name ?? '').trim() || undefined,
    email: String(raw.email ?? '').trim() || undefined,
    isActive: raw.isActive !== undefined ? Boolean(raw.isActive) : Boolean(raw.is_active ?? true),
    role: String(raw.role ?? 'User').trim() || 'User',
    isDefault: raw.isDefault !== undefined ? Boolean(raw.isDefault) : Boolean(raw.is_default ?? false),
  };
}

export default tenantAdminService;
